import { collection, addDoc, writeBatch, doc, getDocs, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import productsNitrilo from '../data/products-nitrilo.json';

/**
 * Migra todos los productos del JSON a Firestore (ELIMINANDO los existentes)
 * Esta es la función "nuclear" - borra todo y recarga desde JSON
 */
export const migrateProductsToFirestore = async () => {
  try {
    console.log('🚀 Iniciando migración de productos...');
    
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    
    // PASO 1: Eliminar todos los productos existentes
    if (!snapshot.empty) {
      console.log(`⏳ Eliminando ${snapshot.size} productos existentes...`);
      const batch = writeBatch(db);
      snapshot.docs.forEach(doc => {
        batch.delete(doc.ref);
      });
      await batch.commit();
      console.log('✅ Productos eliminados');
    }
    
    // PASO 2: Cargar productos desde JSON
    console.log(`⏳ Cargando ${productsNitrilo.length} productos desde JSON...`);
    const newBatch = writeBatch(db);
    let count = 0;

    productsNitrilo.forEach((product) => {
      const docRef = doc(productsRef);
      newBatch.set(docRef, {
        ...product,
        createdAt: new Date(),
        updatedAt: new Date(),
      });
      count++;
    });

    await newBatch.commit();
    console.log(`✅ ¡Migración exitosa! ${count} productos cargados en Firestore`);
    return { success: true, count };
  } catch (error) {
    console.error('❌ Error en la migración:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Obtener todos los productos de Firestore
 */
export const getProductsFromFirestore = async () => {
  try {
    const productsRef = collection(db, 'products');
    const snapshot = await getDocs(productsRef);
    const products = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
    return products;
  } catch (error) {
    console.error('Error al obtener productos:', error);
    return [];
  }
};

/**
 * Obtener un producto específico por ID de Firestore
 * @param {string} productId - ID del documento en Firestore o campo id del producto
 */
export const getProductById = async (productId) => {
  try {
    const productsRef = collection(db, 'products');
    const allProducts = await getDocs(productsRef);
    
    // Buscar por diferentes criterios
    let found = null;
    allProducts.docs.forEach(doc => {
      const data = doc.data();
      
      // 1. Buscar por documentId de Firestore (firestoreId)
      if (doc.id === productId) {
        found = {
          ...data,
          id: data.id,
          firestoreId: doc.id
        };
        return;
      }
      
      // 2. Buscar por el campo 'id' del documento (números del JSON o generados en AdminPanel)
      if (data.id && (data.id === parseInt(productId) || data.id === productId)) {
        found = {
          ...data,
          firestoreId: doc.id
        };
        return;
      }
    });
    
    return found;
  } catch (error) {
    console.error('Error al obtener producto:', error);
    return null;
  }
};

/**
 * Obtener el próximo ID disponible para productos
 */
const getNextProductId = async () => {
  try {
    const productsRef = collection(db, 'products');
    const allProducts = await getDocs(productsRef);
    
    let maxId = 0;
    allProducts.docs.forEach(doc => {
      const data = doc.data();
      if (data.id && typeof data.id === 'number') {
        if (data.id > maxId) {
          maxId = data.id;
        }
      }
    });
    
    return maxId + 1;
  } catch (error) {
    console.error('Error al obtener próximo ID:', error);
    return Math.floor(Math.random() * 10000); // Fallback
  }
};

/**
 * Agregar nuevo producto a Firestore
 */
export const addProduct = async (productData) => {
  try {
    const productsRef = collection(db, 'products');
    const nextId = await getNextProductId();
    
    const docRef = await addDoc(productsRef, {
      ...productData,
      id: nextId, // Agregar ID único
      createdAt: new Date(),
      updatedAt: new Date(),
    });
    return { success: true, id: docRef.id };
  } catch (error) {
    console.error('Error al agregar producto:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Actualizar producto en Firestore
 */
export const updateProduct = async (productId, productData) => {
  try {
    const productRef = doc(db, 'products', productId);
    await updateDoc(productRef, {
      ...productData,
      updatedAt: new Date(),
    });
    return { success: true };
  } catch (error) {
    console.error('Error al actualizar producto:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Eliminar producto de Firestore
 */
export const deleteProduct = async (productId) => {
  try {
    const productRef = doc(db, 'products', productId);
    await deleteDoc(productRef);
    return { success: true };
  } catch (error) {
    console.error('Error al eliminar producto:', error);
    return { success: false, error: error.message };
  }
};
